import React, { useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import { toast } from 'react-toastify';

import Header from '~/Components/Header/Header';
import {
  buscarPropostaTitulosAtualizaVenc,
  simularNovaDataAtualizaVenc,
  lancarAtualizacaoVencimentoTitulos,
  listarGruposEmpresaDoUsuario,
} from '~/Services/Service';

import CabecalhoBusca from './components/CabecalhoBusca';
import PainelDadosProposta from './components/PainelDadosProposta';
import PainelFiltroData from './components/PainelFiltroData';
import PainelModosCalculo from './components/PainelModosCalculo';
import PainelTaxa from './components/PainelTaxa';
import GridTitulos from './components/GridTitulos';
import ModalConfirmacaoLancar from './components/ModalConfirmacaoLancar';

const extrairMensagemErro = (err) => {
  if (!err) return 'Erro inesperado';
  if (typeof err === 'string') return err;
  return err.message || 'Erro inesperado';
};

// W-0537 fix: backend usa Json.NET com cultura invariante e espera ISO 8601.
// MaskedInput do front produz "DD/MM/YYYY" → conversão obrigatória antes de enviar.
const ddmmaaaaParaIso = (str) => {
  if (!str || typeof str !== 'string' || str.length < 10) return null;
  const [dia, mes, ano] = str.split('/');
  if (!dia || !mes || !ano) return null;
  return `${ano}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
};

const AtualizaVencimentoTitulo = () => {
  // Grupos de Empresa do usuário logado (paridade cdGE UAtuaVenc.pas:1036)
  const [gruposEmpresa, setGruposEmpresa] = useState([]);

  // Formulário de busca
  const [ge, setGe] = useState(null);
  const [numeroProposta, setNumeroProposta] = useState('');
  const [dataInicialFiltro, setDataInicialFiltro] = useState('');

  // Dados retornados pelo Buscar
  const [proposta, setProposta] = useState(null);
  const [taxaContext, setTaxaContext] = useState(null);
  const [titulos, setTitulos] = useState([]);

  // Simulação
  const [modoCalculo, setModoCalculo] = useState({ qtdeMeses: '', diaDoMes: '', dataFixa: '' });
  const [titulosAlterados, setTitulosAlterados] = useState({});

  // Taxa editável
  const [taxaAjustada, setTaxaAjustada] = useState(0);
  const [tipoTaxa, setTipoTaxa] = useState('F');

  // UI
  const [loading, setLoading] = useState(false);
  const [modalConfirmacaoOpen, setModalConfirmacaoOpen] = useState(false);

  const geOptions = gruposEmpresa.map((item) => ({
    value: item.codigo,
    label: `${item.codigo} - ${item.apelido}`,
  }));

  // Carrega grupos de empresa do usuário no mount
  useEffect(() => {
    const carregar = async () => {
      try {
        const res = await listarGruposEmpresaDoUsuario();
        setGruposEmpresa(res || []);
      } catch (err) {
        toast.error(`Erro ao carregar grupos de empresa: ${extrairMensagemErro(err)}`);
      }
    };
    carregar();
  }, []);

  // Auto-seleciona GE quando há apenas 1 opção (paridade SetaGE Delphi)
  useEffect(() => {
    if (geOptions.length === 1 && ge === null) {
      setGe(geOptions[0].value);
    }
  }, [geOptions.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => {
    setGe(null);
    setNumeroProposta('');
    setDataInicialFiltro('');
    setProposta(null);
    setTaxaContext(null);
    setTitulos([]);
    setModoCalculo({ qtdeMeses: '', diaDoMes: '', dataFixa: '' });
    setTitulosAlterados({});
    setTaxaAjustada(0);
    setTipoTaxa('F');
    setModalConfirmacaoOpen(false);
  };

  const handleBuscar = async () => {
    if (!ge) {
      toast.warn('Selecione um Grupo Empresa');
      return;
    }
    if (!numeroProposta || Number(numeroProposta) <= 0) {
      toast.warn('Informe o número da proposta');
      return;
    }

    setLoading(true);
    setTitulosAlterados({});
    try {
      const payload = {
        codigo_grupo_empresa: ge,
        codigo_proposta: Number(numeroProposta),
        // W-0537 fix: converter DD/MM/YYYY → YYYY-MM-DD (Json.NET).
        data_inicial_filtro: ddmmaaaaParaIso(dataInicialFiltro),
      };
      const res = await buscarPropostaTitulosAtualizaVenc(payload);
      if (!res.proposta) {
        toast.warn('Proposta não encontrada');
        setProposta(null);
        setTaxaContext(null);
        setTitulos([]);
        return;
      }
      setProposta(res.proposta);
      setTaxaContext(res.taxa);
      setTitulos(res.titulos || []);
      setTaxaAjustada(res.taxa ? res.taxa.taxa_default : 0);
      setTipoTaxa(res.taxa ? res.taxa.tipo_taxa : 'F');
    } catch (err) {
      const mensagem = extrairMensagemErro(err);
      toast.error(mensagem);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltrar = () => {
    if (!dataInicialFiltro) {
      toast.warn('Informe a data inicial');
      return;
    }
    handleBuscar();
  };

  const handleSimular = async () => {
    const temModo =
      (modoCalculo.qtdeMeses && Number(modoCalculo.qtdeMeses) > 0) ||
      (modoCalculo.diaDoMes && Number(modoCalculo.diaDoMes) > 0) ||
      !!modoCalculo.dataFixa;

    if (!temModo) {
      toast.warn('Informe ao menos um modo de cálculo');
      return;
    }
    if (titulos.length === 0) {
      toast.warn('Nenhum título elegível para simular');
      return;
    }

    setLoading(true);
    try {
      // W-0537 fix: payload precisa bater com SimularAtualizaVencimentoRequest do backend
      // (campos no nível raiz, não aninhados em "modo"; "titulos" como objetos completos,
      // não array de ids; conversão de data_fixa para ISO).
      const payload = {
        codigo_proposta: Number(numeroProposta),
        codigo_grupo_empresa: ge,
        qtd_meses: modoCalculo.qtdeMeses ? Number(modoCalculo.qtdeMeses) : 0,
        dia_do_mes: modoCalculo.diaDoMes ? Number(modoCalculo.diaDoMes) : 0,
        data_fixa: ddmmaaaaParaIso(modoCalculo.dataFixa),
        taxa: taxaAjustada || 0,
        tipo_juros: tipoTaxa || 'F',
        titulos: titulos.map((t) => ({
          codigo_titulo: t.codigo_titulo,
          data_vencimento: t.data_vencimento,
          valor: t.valor,
          liquido: t.liquido,
          codigo_ativo: t.codigo_ativo,
        })),
      };
      const res = await simularNovaDataAtualizaVenc(payload);
      const novoMapa = {};
      // Backend retorna { titulos: [...] } com nova_data em ISO
      (res.titulos || []).forEach((r) => {
        novoMapa[r.codigo_titulo] = r.nova_data;
      });
      setTitulosAlterados(novoMapa);
      if (Object.keys(novoMapa).length === 0) {
        toast.info('Nenhum título alterado pela simulação (verifique modo de cálculo).');
      }
    } catch (err) {
      toast.error(`Erro ao simular: ${extrairMensagemErro(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleNovaDataChange = (codigoTitulo, novaData) => {
    setTitulosAlterados((prev) => ({ ...prev, [codigoTitulo]: novaData }));
  };

  const handleAbrirConfirmacao = () => {
    const titulosComAlteracao = titulos.filter(
      (t) => titulosAlterados[t.codigo_titulo] && titulosAlterados[t.codigo_titulo] !== t.data_vencimento,
    );
    if (titulosComAlteracao.length === 0) {
      toast.info('Nenhum vencimento foi alterado');
      return;
    }
    setModalConfirmacaoOpen(true);
  };

  const handleLancar = async () => {
    setModalConfirmacaoOpen(false);
    const titulosPayload = titulos
      .filter(
        (t) => titulosAlterados[t.codigo_titulo] && titulosAlterados[t.codigo_titulo] !== t.data_vencimento,
      )
      .map((t) => ({
        codigo_titulo: t.codigo_titulo,
        nova_data_vencimento: titulosAlterados[t.codigo_titulo],
      }));

    if (titulosPayload.length === 0) {
      toast.info('Nenhum vencimento foi alterado');
      return;
    }

    if (titulosPayload.length > 200) {
      toast.warn('Limite de 200 títulos por operação. Filtre e tente novamente.');
      return;
    }

    setLoading(true);
    try {
      await lancarAtualizacaoVencimentoTitulos({
        codigo_grupo_empresa: ge,
        codigo_proposta: Number(numeroProposta),
        taxa: taxaAjustada,
        tipo_taxa: tipoTaxa,
        painel_taxa_visivel: taxaContext ? taxaContext.painel_taxa_visivel : false,
        titulos: titulosPayload,
      });
      toast.success('Vencimentos atualizados com sucesso');
      resetForm();
    } catch (err) {
      const mensagem = extrairMensagemErro(err);
      if (mensagem.includes('modificados por outro usuário')) {
        toast.error(mensagem, { autoClose: 10000 });
        handleBuscar();
      } else if (mensagem.includes('Limite')) {
        toast.warn(mensagem);
      } else {
        toast.error(`Erro ao processar atualização: ${mensagem}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid>
      <Header title="Atualizar Vencimento de Título" />

      <CabecalhoBusca
        ge={ge}
        geOptions={geOptions}
        onGeChange={setGe}
        numeroProposta={numeroProposta}
        onNumeroProposta={setNumeroProposta}
        onBuscar={handleBuscar}
        loading={loading}
      />

      {proposta && (
        <>
          <PainelDadosProposta proposta={proposta} />

          <PainelFiltroData
            dataInicialFiltro={dataInicialFiltro}
            onDataChange={setDataInicialFiltro}
            onFiltrar={handleFiltrar}
            loading={loading}
          />

          <PainelModosCalculo
            modoCalculo={modoCalculo}
            onModoChange={setModoCalculo}
            onSimular={handleSimular}
            loading={loading}
          />

          {taxaContext && taxaContext.painel_taxa_visivel && (
            <PainelTaxa
              taxa={taxaAjustada}
              tipoTaxa={tipoTaxa}
              onTaxaChange={setTaxaAjustada}
              onTipoTaxaChange={setTipoTaxa}
            />
          )}

          {titulos.length > 0 && (
            <GridTitulos
              titulos={titulos}
              titulosAlterados={titulosAlterados}
              onNovaDataChange={handleNovaDataChange}
              onLancar={handleAbrirConfirmacao}
              onCancelar={resetForm}
              loading={loading}
            />
          )}
        </>
      )}

      <ModalConfirmacaoLancar
        show={modalConfirmacaoOpen}
        onClose={() => setModalConfirmacaoOpen(false)}
        onConfirm={handleLancar}
        titulos={titulos}
        titulosAlterados={titulosAlterados}
        taxa={taxaAjustada}
        tipoTaxa={tipoTaxa}
        loading={loading}
      />
    </Container>
  );
};

export default AtualizaVencimentoTitulo;
